import { createContext, useContext, useMemo, useState, useCallback } from 'react';
import {
  canUsePermissionDebug,
  clearViewAsStorage,
  readViewAsFromStorage,
  writeViewAsToStorage,
} from '../utils/permissionDebug';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

function readSavedUser() {
  try {
    const savedUser = localStorage.getItem('userData');
    return savedUser ? JSON.parse(savedUser) : null;
  } catch {
    return null;
  }
}

export const UserProvider = ({ children }) => {
  const [realUser, setRealUser] = useState(() => readSavedUser());

  const [viewAs, setViewAsState] = useState(() => {
    const saved = readSavedUser();
    if (!canUsePermissionDebug(saved)) {
      clearViewAsStorage();
      return null;
    }
    return readViewAsFromStorage();
  });

  const setUserData = useCallback((userData) => {
    console.log('Setting user data in context:', userData);
    setRealUser(userData);
    localStorage.setItem('userData', JSON.stringify(userData));
    if (!canUsePermissionDebug(userData)) {
      setViewAsState(null);
      clearViewAsStorage();
    }
  }, []);

  const clearUser = useCallback(() => {
    setRealUser(null);
    setViewAsState(null);
    localStorage.removeItem('userData');
    clearViewAsStorage();
  }, []);

  const setViewAs = useCallback(
    (payload) => {
      if (!canUsePermissionDebug(realUser)) return;
      if (!payload?.user) {
        setViewAsState(null);
        clearViewAsStorage();
        return;
      }
      const next = {
        mode: payload.mode || 'user',
        label: payload.label || payload.user?.nombre || payload.user?.tipo_usuario || 'Simulación',
        user: payload.user,
      };
      setViewAsState(next);
      writeViewAsToStorage(next);
    },
    [realUser],
  );

  const clearViewAs = useCallback(() => {
    setViewAsState(null);
    clearViewAsStorage();
  }, []);

  const user = viewAs?.user || realUser;
  const isViewAsActive = Boolean(viewAs?.user);

  const value = useMemo(
    () => ({
      user,
      realUser,
      viewAs,
      isViewAsActive,
      setUserData,
      clearUser,
      setViewAs,
      clearViewAs,
    }),
    [user, realUser, viewAs, isViewAsActive, setUserData, clearUser, setViewAs, clearViewAs],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
